import { useQuery } from '@tanstack/react-query';
import ChatService from '../services/chat.service';

const chatService = new ChatService();

/**
 * Custom hook to fetch a signed document URL using React Query's query.
 *
 * @param documentId - The ID of the document to fetch the signed URL for.
 * @returns An object containing the query state and data.
 */
export const useGetSignedDocumentUrl = (documentId?: string, enabled = true) => {
    return useQuery<
        { signed_url: string; expires_at: string; content_type: string }, // Return type
        Error // Error type
    >({
        queryKey: ['signedDocumentUrl', documentId], // Query key
        queryFn: async () => {
            if (!documentId) {
                throw new Error('Document ID is required to fetch the signed URL');
            }
            try {
                const result = await chatService.getSignedDocmentUrl(documentId);
                return result;
            } catch (error) {
                console.error('❌ [useGetSignedDocumentUrl] Error fetching signed document URL:', error);
                throw error;
            }
        },
        enabled: enabled && !!documentId, // Only run the query if enabled and documentId is provided
        staleTime: 5 * 60 * 1000, // Cache for 5 minutes since signed URLs have expiration
        retry: (failureCount, error) => {
            // Don't retry on authentication errors or if document doesn't exist
            if (error instanceof Error && (error.message.includes('401') || error.message.includes('404'))) {
                return false;
            }
            return failureCount < 2; // Retry up to 2 times for other errors
        },
    });
};
